import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { usePermission } from '@/hooks/usePermission';
import { NAV_ITEMS, isNavParent, type NavItem, type NavParentItem } from '@/config/navigation.config';

interface NavListProps {
  onNavigate?: () => void;
  collapsed?: boolean;
}

function isChildActive(item: NavParentItem, pathname: string): boolean {
  return item.children.some((child) => pathname.startsWith(child.path));
}

export function NavList({ onNavigate, collapsed = false }: NavListProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAnyRole } = usePermission();
  const [openGroup, setOpenGroup] = useState<string | null>(() => {
    const activeParent = NAV_ITEMS.find(
      (item) => isNavParent(item) && isChildActive(item, location.pathname),
    );
    return activeParent ? activeParent.label : null;
  });
  const [flyoutAnchor, setFlyoutAnchor] = useState<HTMLElement | null>(null);
  const [flyoutItem, setFlyoutItem] = useState<NavParentItem | null>(null);

  const visibleItems = NAV_ITEMS.filter(
    (item: NavItem) => !item.roles || hasAnyRole(item.roles),
  );

  const handleNavigate = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  const openFlyout = (event: MouseEvent<HTMLElement>, item: NavParentItem) => {
    setFlyoutAnchor(event.currentTarget);
    setFlyoutItem(item);
  };

  const closeFlyout = () => {
    setFlyoutAnchor(null);
    setFlyoutItem(null);
  };

  return (
    <List sx={{ px: 1.5, py: 1 }} component="nav">
      {visibleItems.map((item) => {
        if (isNavParent(item)) {
          const active = isChildActive(item, location.pathname);
          const expanded = openGroup === item.label;
          const ParentIcon = item.icon;

          return (
            <li key={item.label} style={{ listStyle: 'none' }}>
              <Tooltip title={collapsed ? t(item.label) : ''} placement="right">
                <ListItemButton
                  onClick={(event) =>
                    collapsed ? openFlyout(event, item) : setOpenGroup(expanded ? null : item.label)
                  }
                  selected={active}
                  sx={{ borderRadius: 1.5, mb: 0.5, justifyContent: collapsed ? 'center' : 'flex-start' }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                    <ParentIcon fontSize="small" />
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText primary={t(item.label)} />
                      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              {!collapsed && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = location.pathname.startsWith(child.path);
                      return (
                        <ListItemButton
                          key={child.path}
                          onClick={() => handleNavigate(child.path)}
                          selected={childActive}
                          sx={{ borderRadius: 1.5, mb: 0.5, pl: 4.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <ChildIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={t(child.label)} />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </li>
          );
        }

        const Icon = item.icon;
        const active = location.pathname.startsWith(item.path);

        return (
          <Tooltip key={item.path} title={collapsed ? t(item.label) : ''} placement="right">
            <ListItemButton
              onClick={() => handleNavigate(item.path)}
              selected={active}
              sx={{ borderRadius: 1.5, mb: 0.5, justifyContent: collapsed ? 'center' : 'flex-start' }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              {!collapsed && <ListItemText primary={t(item.label)} />}
            </ListItemButton>
          </Tooltip>
        );
      })}

      <Menu anchorEl={flyoutAnchor} open={Boolean(flyoutAnchor)} onClose={closeFlyout} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {flyoutItem?.children.map((child) => {
          const ChildIcon = child.icon;
          return (
            <MenuItem
              key={child.path}
              onClick={() => {
                closeFlyout();
                handleNavigate(child.path);
              }}
            >
              <ListItemIcon>
                <ChildIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t(child.label)}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </List>
  );
}
